/**
 * Clinic operating days (#133) — single source of truth for "is this clinic
 * open on this date?".
 *
 * Stored on the clinic doc as `operatingDays: DayKey[]` (e.g. ["mon".."fri"]).
 * A missing/invalid/empty value means NO signal — the clinic is treated as
 * open every day, which is exactly the pre-#133 behavior. That keeps legacy
 * docs honest and makes "closed" always an explicit admin choice.
 *
 * Imported by:
 *  - functions/src/appointmentPull/processAllOrgs.ts (skip pulls on closed days)
 *  - functions/src/appointmentPull/revolutionIngest.service.ts (zero patients on a closed day ≠ failure)
 *  - functions/src/staff/listClinicCoverage.ts (heat map "closed" cells)
 *  - src/components/ClinicsList.tsx (the day-pills control)
 */

export type DayKey = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";

/** Week in render order (Sun-first, matching the heat map's column order). */
export const WEEK: readonly DayKey[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export const DAY_LABEL: Record<DayKey, string> = {
  sun: "Sunday", mon: "Monday", tue: "Tuesday", wed: "Wednesday",
  thu: "Thursday", fri: "Friday", sat: "Saturday",
};

/** Narrow an unknown Firestore value to a clean DayKey[], or null if it
 *  carries no usable signal (absent, wrong type, empty, or all-garbage). */
export function parseOperatingDays(raw: unknown): DayKey[] | null {
  if (!Array.isArray(raw)) return null;
  const days = raw.filter((d): d is DayKey => WEEK.includes(d as DayKey));
  return days.length > 0 ? [...new Set(days)] : null;
}

/**
 * Weekday of a clinic-local date string. Accepts the two formats that flow
 * through the pipeline: MM/DD/YYYY (run ids, run docs) and YYYY-MM-DD
 * (heat-map window). Pure calendar arithmetic via Date.UTC — the string is
 * already clinic-local, so no timezone conversion may happen here.
 * Returns null for unparseable input.
 */
export function dayKeyOf(dateStr: string): DayKey | null {
  let y: number; let m: number; let d: number;
  let match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateStr);
  if (match) {
    m = Number(match[1]); d = Number(match[2]); y = Number(match[3]);
  } else {
    match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!match) return null;
    y = Number(match[1]); m = Number(match[2]); d = Number(match[3]);
  }
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  if (Number.isNaN(dt.getTime())) return null;
  return WEEK[dt.getUTCDay()];
}

/**
 * Is the clinic open on this clinic-local date?
 * No operating-days signal → open (legacy behavior). Unparseable date → open
 * (never let a malformed string silently suppress a pull).
 */
export function isOpenOn(operatingDaysRaw: unknown, dateStr: string): boolean {
  const days = parseOperatingDays(operatingDaysRaw);
  if (!days) return true;
  const key = dayKeyOf(dateStr);
  if (!key) return true;
  return days.includes(key);
}
